import { Nullable } from "sonobello.utilities.react";

import IMedicalQualification from "./IMedicalQualification";
import IMedicalQuestionnaireCompleteEvaluator from "./IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "./IMedicalQualificationNonCandidateEvaluator";

/** Configures behaviors for {@link ComposableMedicalFormEvaluator}. */
export interface IComposableMedicalFormEvaluatorConfig {
  /** A list of all question IDs that are part of the form. */
  allFormQuestionIds: string[];
  /** A list of question IDs that should be ignored entirely for non-candidate evaluations. */
  nonCandidateIgnoredQuestionIds: Nullable<string[]>;
  /** Custom definitions of non-candidacy for the given form state, keyed by the question id to which question they apply
   * to. Returns `true` if the question indicates non-candidacy, `false` if it does not.
   */
  nonCandidateQuestionPredicates: Nullable<Record<string, (currentFormState: IMedicalQualification) => boolean>>;
  /** Custom definitions of required questions for the given form state, keyed by the question id to which question they
   * apply to. Returns `true` if the question is required, `false` if it is not.
   */
  requiredQuestionPredicates: Nullable<Record<string, (currentFormState: IMedicalQualification) => boolean>>;
}

/** An evaluator for medical forms that is entirely composable with custom behaviors.
 * @remarks unless configured otherwise, any answer of `true` is treated as a non-candidate response,
 * and all questions are treated as required.
 */
class ComposableMedicalFormEvaluator
  implements IMedicalQuestionnaireCompleteEvaluator, IMedicalQualificationNonCandidateEvaluator
{
  private readonly allFormQuestionIds: string[];
  private readonly nonCandidateQuestionPredicates: Record<string, (currentFormState: IMedicalQualification) => boolean>;
  private readonly requiredQuestionPredicates: Record<string, (currentFormState: IMedicalQualification) => boolean>;

  constructor(config: IComposableMedicalFormEvaluatorConfig) {
    this.allFormQuestionIds = [...config.allFormQuestionIds];
    this.nonCandidateQuestionPredicates = { ...config.nonCandidateQuestionPredicates };
    this.requiredQuestionPredicates = { ...config.requiredQuestionPredicates };

    config.nonCandidateIgnoredQuestionIds?.forEach(
      ignoredId => (this.nonCandidateQuestionPredicates[ignoredId] = () => false)
    );
  }

  isComplete(currentFormState: IMedicalQualification) {
    return this.allFormQuestionIds.every(questionId => {
      if (
        !this.requiredQuestionPredicates[questionId] ||
        this.requiredQuestionPredicates[questionId](currentFormState)
      ) {
        const answer = currentFormState[questionId];
        return !(answer === null || answer === undefined);
      }
      return true;
    });
  }

  isNonCandidate(currentFormState: IMedicalQualification) {
    return this.allFormQuestionIds.some(questionId => {
      if (this.nonCandidateQuestionPredicates[questionId])
        return this.nonCandidateQuestionPredicates[questionId](currentFormState);

      return currentFormState[questionId] === true;
    });
  }
}

export default ComposableMedicalFormEvaluator;
